import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Save, School, ArrowLeft } from "lucide-react";
import { handleUpdate, handleGet, handleSend, handleUpdateEcoleLogo } from "../../../controllers/controller.all";
import { getImageUrl } from "../../../services/service.api";

export default function EcoleForm() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    // Si un ID est passé en paramètre, on l'utilise (Super Admin), sinon on utilise l'école de l'utilisateur
    const ecoleId = id || (user?.role !== "SUPER_ADMIN" ? (user?.ecole?._id || user?.ecole) : null);

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        nom: "",
        numero: "",
        pays: "",
        ville: "",
        sigle: "",
        adresse: "",
        mail: "",
        site_web: "",
        logo: "",
        orange_money: "",
        moov_money: "",
        rib: ""
    });
    const [imageHash, setImageHash] = useState(Date.now());

    const fetchEcole = async () => {
        setLoading(true);
        await handleGet(`/ecole/${ecoleId}`, (res) => {
            if (res?.ecole) {
                setFormData({
                    nom: res.ecole.nom || "",
                    numero: res.ecole.numero || "",
                    pays: res.ecole.pays || "",
                    ville: res.ecole.ville || "",
                    sigle: res.ecole.sigle || "",
                    adresse: res.ecole.adresse || "",
                    mail: res.ecole.mail || "",
                    site_web: res.ecole.site_web || "",
                    logo: res.ecole.logo || "",
                    orange_money: res.ecole.orange_money || "",
                    moov_money: res.ecole.moov_money || "",
                    rib: res.ecole.rib || ""
                });
                
                // Mettre à jour Redux pour que le header/sidebar reflète immédiatement les changements
                if (user?.role !== "SUPER_ADMIN" && (!user?.ecole || user?.ecole?._id === ecoleId || user?.ecole === ecoleId)) {
                    dispatch({ type: "user-update", value: { ...user, ecole: res.ecole } });
                }
            }
        }, token);
        setLoading(false);
    };

    const getSafeImageUrl = (ref) => {
        if (!ref) return "";
        if (ref.startsWith("data:")) return ref;
        const url = getImageUrl(ref);
        return `${url}?t=${imageHash}`;
    };

    useEffect(() => {
        if (ecoleId) {
            fetchEcole();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ecoleId, token]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (ecoleId) {
                await handleUpdateEcoleLogo(file, ecoleId, token, (freshEcole) => {
                    if (freshEcole) {
                        setImageHash(Date.now());
                        setFormData({ ...formData, logo: freshEcole.logo });
                        fetchEcole();
                    }
                });
            } else {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFormData({ ...formData, logo: reader.result });
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let res;
        if (ecoleId) {
            res = await handleUpdate(e, `/ecole/${ecoleId}`, formData, setLoading, token);
        } else {
            res = await handleSend(e, `/ecole`, formData, setLoading, token);
        }
        
        if (res && (res.state === "success" || res.status === "success")) {
            if (user?.role === "SUPER_ADMIN") {
                navigate("/super-admin/ecoles");
            } else if (ecoleId) {
                // Refetch and sync Redux so phone/plan changes are visible without re-login
                fetchEcole();
            }
        }
    };

    return (
        <div className="flex flex-col gap-6 w-full max-w-3xl mx-auto">
            <div className="flex items-center gap-3 border-b border-gray-200 pb-4 flex-wrap">
                {user?.role === "SUPER_ADMIN" && (
                    <button
                        type="button"
                        onClick={() => navigate("/super-admin/ecoles")}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition flex-shrink-0"
                        title="Retour"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                )}
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg flex-shrink-0">
                    <School className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Profil de l'École</h1>
                    <p className="text-sm text-gray-500">Mettez à jour les informations de votre établissement.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-gray-700">Nom complet de l'établissement *</label>
                        <input required type="text" name="nom" value={formData.nom} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                    
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Sigle</label>
                        <input type="text" name="sigle" value={formData.sigle} onChange={handleChange} placeholder="Ex: KY" className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Numéro de téléphone *</label>
                        <input required type="text" name="numero" value={formData.numero} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Email officiel</label>
                        <input type="email" name="mail" value={formData.mail} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Site Web</label>
                        <input type="url" name="site_web" value={formData.site_web} onChange={handleChange} placeholder="https://..." className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Logo de l'établissement</label>
                        <div className="flex items-center gap-4">
                            {formData.logo ? (
                                <img src={getSafeImageUrl(formData.logo)} alt="Logo" className="h-16 w-16 rounded-lg object-cover border border-gray-200" />
                            ) : (
                                <div className="h-16 w-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-400">
                                    <School className="h-6 w-6" />
                                </div>
                            )}
                            <input 
                                type="file" 
                                accept="image/*" 
                                onChange={handleFileChange} 
                                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer" 
                            />
                        </div>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-gray-700">Adresse complète</label>
                        <input type="text" name="adresse" value={formData.adresse} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Ville</label>
                        <input type="text" name="ville" value={formData.ville} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Pays</label>
                        <input type="text" name="pays" value={formData.pays} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>

                    <div className="col-span-1 md:col-span-2 mt-4 pb-4 mb-2 border-b border-gray-200">
                        <h2 className="text-lg font-medium text-gray-950">Coordonnées de Paiement</h2>
                        <p className="text-xs text-gray-500 mt-1">Configurez les moyens de paiement pour la scolarité (affichés sur les applications mobiles des parents/élèves).</p>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Numéro Orange Money</label>
                        <input type="text" name="orange_money" value={formData.orange_money} onChange={handleChange} placeholder="Ex: +22177..." className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Numéro Moov Money</label>
                        <input type="text" name="moov_money" value={formData.moov_money} onChange={handleChange} placeholder="Ex: +22170..." className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-gray-700">Relevé d'Identité Bancaire (RIB)</label>
                        <textarea name="rib" value={formData.rib} onChange={handleChange} rows={3} placeholder="Ex: SN012 01234 012345678901 12" className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white resize-none" />
                    </div>
                </div>

                <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition shadow-sm disabled:opacity-50">
                        <Save className="h-4 w-4" />
                        {loading ? "Mise à jour..." : "Mettre à jour"}
                    </button>
                </div>
            </form>
        </div>
    );
}
